import type { Branding } from '@sunucupark/shared';
import { ClientProvider, createQueryClient } from '@sunucupark/client-react';
import { QueryClientProvider } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { I18nextProvider } from 'react-i18next';
import type { WebClient } from './client';
import { ErrorBoundary } from './error-boundary';
import { i18n } from './i18n';
import { Toaster } from '@sunucupark/ui';
import { RealtimeBridge } from './realtime-bridge';
import { UnreadBadge } from './UnreadBadge';
import { BrandingProvider } from './use-branding';

export interface ProvidersProps {
  client: WebClient;
  branding: Branding;
  children: ReactNode;
}

/**
 * Everything the tree needs, in one place: the injected client, the query cache
 * shared with the mobile app's rules, translations and the error boundary.
 */
export function Providers({ client, branding, children }: ProvidersProps) {
  const { t } = useTranslation();
  const toastLabels = { region: t('app.notifications'), close: t('app.close') };
  // The browser refetches when the tab comes back; mobile does not.
  const queryClient = useMemo(() => createQueryClient({ refetchOnWindowFocus: true }), []);
  return (
    <ErrorBoundary>
      <I18nextProvider i18n={i18n}>
        <ClientProvider client={client}>
          <BrandingProvider initial={branding} client={client}>
            <QueryClientProvider client={queryClient}>
              <RealtimeBridge />
              <UnreadBadge />
              {children}
              <Toaster label={toastLabels.region} closeLabel={toastLabels.close} />
            </QueryClientProvider>
          </BrandingProvider>
        </ClientProvider>
      </I18nextProvider>
    </ErrorBoundary>
  );
}
